import { useEffect, useState } from "react";
import {
  Modal,
  Button,
  Form,
  Spinner,
  Alert,
  Row,
  Col,
  Card,
} from "react-bootstrap";
import axios from "axios";
import { BASE_URL, HEADERS } from "../../../utils/constant";

const IssueStockModal = ({ show, handleClose, servicerequest }) => {
  const [materials, setMaterials] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [fetching, setFetching] = useState(false);

  const fetchMaterials = async () => {
    if (!servicerequest?.id) return;

    setFetching(true);
    setError("");
    setSuccess("");

    try {
      const response = await axios.get(
        `${BASE_URL}/api/service_request/${servicerequest.id}/materials/`,
        { headers: HEADERS },
      );

      const materialList = response.data?.materials || [];

      setMaterials(
        materialList.map((mat) => ({
          id: mat.id,
          name: mat.name,
          requiredQty: Number(mat.quantity) || 0,
          pendingQty: Number(mat.pending_quantity) || 0,
          approvedQty: Number(mat.approved_quantity) || 0,
          issueQty: 0,
          checked: false,
          status: mat.status || "Pending",
        })),
      );
    } catch (err) {
      console.error(err);
      setError(
        err.response?.data?.error ||
          "Failed to fetch materials. Please try again.",
      );
    } finally {
      setFetching(false);
    }
  };

  useEffect(() => {
    if (show) fetchMaterials();
  }, [show, servicerequest?.id]);

  const handleCheck = (index) => {
    const updated = [...materials];
    const mat = updated[index];

    if (mat.status === "Approved") return;

    mat.checked = !mat.checked;
    if (!mat.checked) {
      mat.issueQty = 0;
    }
    setMaterials(updated);
  };

  const handleQtyChange = (index, value) => {
    const updated = [...materials];
    const qty = Number(value);
    updated[index].issueQty = Math.max(qty, 0);
    setMaterials(updated);
  };

  const handleSubmit = async () => {
    const selected = materials.filter((m) => m.checked);

    if (selected.length === 0) {
      setError("Please select at least one material to issue.");
      return;
    }

    setLoading(true);
    setError("");
    setSuccess("");

    try {
      const payload = {
        materials: selected.map((m) => ({
          id: m.id,
          name: m.name,
          requested_quantity: m.issueQty,
        })),
      };

      const response = await axios.put(
        `${BASE_URL}/api/service_request/${servicerequest.id}/issue/`,
        payload,
        { headers: HEADERS },
      );

      setSuccess(response.data.message || "Materials issued successfully!");
      await fetchMaterials();
    } catch (err) {
      console.error(err);
      setError(
        err.response?.data?.error ||
          "Failed to issue materials. Please try again.",
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal show={show} onHide={handleClose} size="md" centered>
      <Modal.Header closeButton>
        <Modal.Title>
          Issue Materials - Request ID {servicerequest?.id}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {fetching ? (
          <div className="text-center my-4">
            <Spinner animation="border" />
            <div className="mt-2">Loading materials...</div>
          </div>
        ) : (
          <>
            {error && <Alert variant="danger">{error}</Alert>}
            {success && <Alert variant="success">{success}</Alert>}

            {materials.length === 0 ? (
              <div className="text-center text-muted py-3">
                No materials found for this request.
              </div>
            ) : (
              <div className="d-flex flex-column gap-3">
                {materials.map((mat, index) => (
                  <Card
                    key={mat.id || index}
                    className={`p-2 shadow-sm border-0 ${
                      mat.status === "Approved" ? "bg-light" : ""
                    }`}
                  >
                    <Row className="align-items-center">
                      <Col xs={1}>
                        <Form.Check
                          type="checkbox"
                          checked={mat.checked}
                          onChange={() => handleCheck(index)}
                          disabled={mat.status === "Approved"}
                        />
                      </Col>

                      <Col xs={6}>
                        <strong>{mat.name}</strong>

                        <div
                          className="text-muted"
                          style={{ fontSize: "0.85rem" }}
                        >
                          <div>Required: {mat.requiredQty}</div>

                          {mat.approvedQty > 0 && (
                            <div>
                              Pending: {mat.requiredQty - mat.approvedQty}
                            </div>
                          )}

                          <div>
                            Status:{" "}
                            <span
                              className={`fw-semibold ${
                                mat.status === "Pending"
                                  ? "text-warning"
                                  : mat.status === "Issued"
                                    ? "text-success"
                                    : mat.status === "Approved"
                                      ? "text-primary"
                                      : "text-secondary"
                              }`}
                            >
                              {mat.status}
                            </span>
                          </div>
                        </div>
                      </Col>

                      <Col xs={5}>
                        <Form.Control
                          type="number"
                          min={0}
                          max={
                            mat.pendingQty > 0
                              ? mat.pendingQty
                              : mat.requiredQty
                          }
                          value={mat.issueQty}
                          onChange={(e) => {
                            let val = Number(e.target.value);
                            const maxVal =
                              mat.pendingQty > 0
                                ? mat.pendingQty
                                : mat.requiredQty;
                            if (val > maxVal) val = maxVal;
                            handleQtyChange(index, val);
                          }}
                          size="sm"
                          disabled={!mat.checked || mat.status === "Approved"}
                        />
                        <div
                          className="text-muted"
                          style={{ fontSize: "0.75rem" }}
                        >
                          Max:{" "}
                          {mat.pendingQty > 0
                            ? mat.pendingQty
                            : mat.requiredQty}
                        </div>
                      </Col>
                    </Row>
                  </Card>
                ))}
              </div>
            )}
          </>
        )}
      </Modal.Body>

      <Modal.Footer>
        <Button
          variant="secondary"
          onClick={handleClose}
          disabled={loading || fetching}
        >
          Close
        </Button>
        <Button
          variant="primary"
          onClick={handleSubmit}
          disabled={loading || fetching}
        >
          {loading ? (
            <Spinner animation="border" size="sm" />
          ) : (
            "Issue Selected"
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default IssueStockModal;
